import { useState } from "react";
import { useAllReferrals } from "../../services/hooks/getAdminapis";
import { BsBank } from "react-icons/bs";
import instance from "../../services/endpoint";
import Loader from "../utilComponents/loader";
import ShimmerLoader from "../utilComponents/shimmer";
import { TbFileDownload } from "react-icons/tb";
import bonusIcon from "../../images/bonus.png";
import { toast } from "react-toastify";

const Referral = ({ searchQuery }) => {
  const [openReferralId, setOpenReferralId] = useState(null);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [isApproving, setIsApproving] = useState(false);

  const { data: myReferrals, isLoading, error } = useAllReferrals();
  const referralList = myReferrals?.referrals || [];
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const filteredReferrals = referralList.filter((ref) => {
    const q = searchQuery.toLowerCase();
    return (
      ref.fullName.toLowerCase().includes(q) ||
      ref.referrerAccountID.toLowerCase().includes(q) ||
      ref.referralID.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filteredReferrals.length / itemsPerPage);
  const paginatedReferrals = filteredReferrals.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleApproveReward = async ({ referredAccountID }) => {
    try {
      setIsApproving(true);
      await instance.patch("/api/v1/referrals/approveReward", {
        accountID: referredAccountID,
      });

      // Update local state
      if (openReferralId && myReferrals) {
        const referralIndex = myReferrals.referrals.findIndex(
          (r) => r.referralID === openReferralId
        );
        if (referralIndex !== -1) {
          const userIndex = myReferrals.referrals[
            referralIndex
          ].referredUsers.findIndex((u) => u.accountID === referredAccountID);
          if (userIndex !== -1) {
            myReferrals.referrals[referralIndex].referredUsers[
              userIndex
            ].rewardApproved = true;
          }
        }
      }

      toast.success("Reward approved successfully");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Approval failed");
    } finally {
      setIsApproving(false);
      setSelectedApproval(null);
    }
  };

  // ============ Withdraw Details Popup ============
  const [userWithdrawDetails, setUserWithdrawDetails] = useState(null);
  const [accountID, setAccountID] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [findLoading, setFindLoading] = useState(false);

  const openPopup = () => setIsPopupOpen(true);
  const closePopup = () => setIsPopupOpen(false);

  const findWithdrawDetails = async () => {
    const trimmed = accountID.trim();
    if (!trimmed) {
      toast.info("Please enter an Account ID");
      return;
    }

    setFindLoading(true);
    try {
      const response = await instance.post(
        "/api/v1/withdrawDetails/findWithdrawDetails",
        {
          AccountID: trimmed,
        }
      );
      setUserWithdrawDetails(response.data.decryptedWithdrawData);
    } catch (err) {
      toast.error("Error finding withdrawal details");
      console.error(err);
    } finally {
      setFindLoading(false);
    }
  };

  // ============== CSV Download ==============
 const downloadCSV = () => {
  const headers = [
    "Referrer Name",
    "Referrer Account ID",
    "Referral ID",
    "Total Referred Users",
  ];

  const escapeCSV = (value) => {
    if (value === null || value === undefined) return '""';
    const str = value.toString();
    return `"${str.replace(/"/g, '""')}"`; // Escape quotes and wrap in quotes
  };

  const rows = filteredReferrals.map((ref) => [
    escapeCSV(ref.fullName),
    escapeCSV(ref.referrerAccountID),
    escapeCSV(ref.referralID),
    escapeCSV(ref.referredUsers?.length || 0),
  ]);

  const csv = [headers.map(escapeCSV), ...rows]
    .map((r) => r.join(","))
    .join("\n");

  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" }); // BOM for Excel
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "referrals.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

  return (
    <div>
      <h2>Referral Management</h2>

      {isLoading && <Loader />}

      <div className="scrollable-table">
        {!openReferralId ? (
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "row-reverse",
                justifyContent: "space-between",
                marginBottom: "1rem",
              }}>
              <button
                onClick={downloadCSV}
                className="downloadBtn"
                style={{ alignSelf: "flex-end" }}>
                <TbFileDownload />
              </button>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Referrer Name</th>
                  <th>Referrer Account ID</th>
                  <th>Referral ID</th>
                  <th>Rewards to Approve</th>
                  <th>Referred User</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="5">
                      <ShimmerLoader width="100%" height="30px" />
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="5" style={{ color: "red" }}>
                      {error?.response?.data?.message ||
                        error?.message ||
                        "Failed to fetch referrals"}
                    </td>
                  </tr>
                ) : paginatedReferrals.length === 0 ? (
                  <tr>
                    <td colSpan="5">No referrals found</td>
                  </tr>
                ) : (
                  paginatedReferrals.map((ref) => (
                    <tr key={ref.referralID}>
                      <td>{ref.fullName}</td>
                      <td>{ref.referrerAccountID}</td>
                      <td>{ref.referralID}</td>
                      <td>
                        {
                          ref.referredUsers?.filter(
                            (user) => user.rewardApproved === false
                          ).length
                        }
                      </td>

                      <td>
                        <button
                          onClick={() => setOpenReferralId(ref.referralID)}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <div className="d-flex justify-content-center mt-3">
              <div className="pagination">
                {[...Array(totalPages)].map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentPage(idx + 1)}
                    style={{
                      padding: "6px 12px",
                      margin: "0 4px",
                      backgroundColor:
                        currentPage === idx + 1 ? "#007bff" : "#f0f0f0",
                      color: currentPage === idx + 1 ? "#fff" : "#000",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}>
                    {idx + 1}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <h3 style={{ textAlign: "center" }}>
              Referred Users for{" "}
              <span style={{ color: "orange" }}>{openReferralId}</span>
            </h3>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "1rem",
              }}>
              <button
                style={{ marginTop: "10px" }}
                onClick={() => setOpenReferralId(null)}>
                Back
              </button>
              <button onClick={openPopup} className="downloadBtn">
                <BsBank />
              </button>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Account ID</th>
                  <th>Deposited</th>
                  <th>Deposit Amount</th>
                  <th>Reward</th>
                  <th>Reward Approved</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {myReferrals?.referrals
                  ?.find((r) => r.referralID === openReferralId)
                  ?.referredUsers?.map((user) => (
                    <tr key={user.accountID}>
                      <td>{user.accountID}</td>
                      <td>{user.hasDeposited ? "Yes" : "No"}</td>
                      <td>₹{user.depositAmount}</td>
                      <td>₹{user.rewardAmount}</td>
                      <td>{user.rewardApproved ? "Yes" : "No"}</td>
                      <td>
                        {user.rewardApproved ? (
                          <button
                            disabled
                            style={{ background: "gray", color: "#fff" }}>
                            Approved
                          </button>
                        ) : (
                          <button
                            style={{ backgroundColor: "green", color: "#fff" }}
                            onClick={() =>
                              setSelectedApproval({
                                referrerAccountID: myReferrals.referrals.find(
                                  (r) => r.referralID === openReferralId
                                ).referrerAccountID,
                                referredAccountID: user.accountID,
                              })
                            }>
                            Approve
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* Approval confirmation modal */}
      {selectedApproval && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}>
          <div
            style={{
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "8px",
              textAlign: "center",
              minWidth: "300px",
            }}>
            <img
              src={bonusIcon}
              alt="Bonus"
              className="mb-4"
              height={100}
              width={100}
            />
            <h5>Approve reward for this user?</h5>
            <p>AccountID: {selectedApproval.referredAccountID}</p>
            <div
              style={{
                marginTop: "1rem",
                display: "flex",
                justifyContent: "space-around",
              }}>
              <button
                style={{
                  backgroundColor: "green",
                  color: "white",
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: "4px",
                }}
                onClick={() => handleApproveReward(selectedApproval)}>
                {isApproving ? "Approving..." : "Yes"}
              </button>
              <button
                style={{
                  backgroundColor: "gray",
                  color: "white",
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: "4px",
                }}
                onClick={() => setSelectedApproval(null)}>
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Withdrawal Info Popup */}
      {isPopupOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close-button" onClick={closePopup}>
              x
            </button>
            <div>
              <h3>Find User Withdrawal Details with AccountID</h3>
              <input
                type="text"
                inputMode="numeric"
                className="withdrawDetailsInput"
                value={accountID}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    setAccountID(value);
                  }
                }}
                placeholder="Enter Account ID"
                required
              />
              <button
                onClick={findWithdrawDetails}
                className="withdrawDetailsBtn"
                style={{ cursor: findLoading ? "progress" : "pointer" }}>
                {findLoading ? "finding..." : "Find"}
              </button>
              {userWithdrawDetails && (
                <div className="withdraw-details">
                  <h4>User Withdrawal Details</h4>
                  <p>
                    <strong>Account Holder Name:</strong>{" "}
                    {userWithdrawDetails.account_holder_name}
                  </p>
                  <p>
                    <strong>Account Number:</strong>{" "}
                    {userWithdrawDetails.account_number}
                  </p>
                  <p>
                    <strong>Bank Account Currency:</strong>{" "}
                    {userWithdrawDetails.bank_account_currency}
                  </p>
                  <p>
                    <strong>BIC/Swift Code:</strong>{" "}
                    {userWithdrawDetails.bic_swift_code}
                  </p>
                  <p>
                    <strong>Branch:</strong> {userWithdrawDetails.branch}
                  </p>
                  <p>
                    <strong>BTC Withdraw Address:</strong>{" "}
                    {userWithdrawDetails.btc_withdraw_address}
                  </p>
                  <p>
                    <strong>ETH Withdraw Address:</strong>{" "}
                    {userWithdrawDetails.eth_withdraw_address}
                  </p>
                  <p>
                    <strong>IFSC Code:</strong> {userWithdrawDetails.ifsc_code}
                  </p>
                  <p>
                    <strong>Netteller Address:</strong>{" "}
                    {userWithdrawDetails.netteller_address}
                  </p>
                  <p>
                    <strong>UPI Address:</strong>{" "}
                    {userWithdrawDetails.upi_address}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Referral;
